import React from 'react';
import { MapPin, CalendarBlank, ArrowRight } from 'phosphor-react';
import { motion } from 'framer-motion';
import SFooterDrawer from './SFooterDrawer';
import { formatTime } from '@/utils/calendar-utils';
import { entitlementUsageLabel } from '@/utils/bookingRules';
import './BookingCheckout.css';

export interface BookingCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  errorMessage?: string;
  showPayment?: boolean;
  bookingData: {
    bookingType: string;
    location: { name: string; address: string };
    date: string;
    time: string;
    times: string[];
    requestBoxPro?: boolean;
    additionalServices?: string[];
    coachName?: string;
    coachingType?: string;
    bayId?: string;
    bayName?: string;
  };
  baySponsorName?: string;
  pricing?: {
    fare: number;
  };
  viaEntitlement?: boolean;
  onConfirm: () => void;
}

function fmtTimeRange(times: string[], fallback: string): string {
  if (!times.length) return fallback;
  const startRaw = times[0].split(' - ')[0];
  const endRaw   = times.at(-1)!.split(' - ')[1];
  return `${formatTime(startRaw)} – ${formatTime(endRaw)}`;
}

export const BookingCheckout: React.FC<BookingCheckoutProps> = ({
  isOpen,
  onClose,
  isLoading = false,
  errorMessage,
  showPayment = false,
  bookingData,
  baySponsorName,
  pricing,
  viaEntitlement = false,
  onConfirm,
}) => {
  const timeRange = fmtTimeRange(bookingData.times || [], bookingData.time);
  const bayLabel = bookingData.bayName && baySponsorName
    ? `${bookingData.bayName} | ${baySponsorName}`
    : bookingData.bayName;
  const details = [bookingData.coachName, bayLabel].filter(Boolean);

  return (
    <SFooterDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm and Book"
      height="auto"
      className="bco-drawer"
    >
      <div className="bco">

        {/* Error — shown at top so it's immediately visible */}
        {!isLoading && errorMessage && (
          <div className="bco__error">{errorMessage}</div>
        )}

        {/* Booking type */}
        <h2 className="bco__type">{bookingData.bookingType}</h2>

        {/* Details card */}
        <div className="bco__card">
          {/* Location */}
          <div className="bco__card-row">
            <MapPin size={14} color="#c99535" weight="fill" />
            <span className="bco__card-text">Tee Box · {bookingData.location.name}</span>
          </div>

          <div className="bco__card-sep" />

          {/* Date + time */}
          <div className="bco__card-row">
            <CalendarBlank size={14} color="#c99535" weight="fill" />
            <div className="bco__card-datetime">
              <span className="bco__card-date">{bookingData.date}</span>
              <span className="bco__card-time">{timeRange}</span>
            </div>
          </div>

          {/* Coach / bay */}
          {details.length > 0 && (
            <>
              <div className="bco__card-sep" />
              <div className="bco__card-pills">
                {details.map((d) => (
                  <span key={d} className="bco__pill">{d}</span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Drop-in price / entitlement message — mutually exclusive.
            `bookingData.times` is already the array `fmtTimeRange` reads
            above and the same array v1 puts into the create payload — a
            separate `slotCount` prop is one more thing that can be
            forgotten at the call site or drift from what is actually
            booked. */}
        {viaEntitlement ? (
          <div className="bco__entitlement-row">
            {entitlementUsageLabel(bookingData.times.length)}
          </div>
        ) : (
          showPayment && pricing && (
            <div className="bco__price-row">
              <span className="bco__price-label">Total</span>
              <span className="bco__price-amount">${pricing.fare.toFixed(2)}</span>
            </div>
          )
        )}

        {/* CTA */}
        <motion.button
          className="bco__cta"
          onClick={onConfirm}
          disabled={isLoading}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading
            ? 'Confirming your booking…'
            : <><span>Confirm and Book</span><ArrowRight size={15} weight="bold" /></>
          }
        </motion.button>

      </div>
    </SFooterDrawer>
  );
};

export default BookingCheckout;
